import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../context/AuthContext'

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt     = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '—'
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—'
const num     = (n) => n != null ? Number(n).toLocaleString() : '0'
const money   = (n) => n != null ? `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'

// Total earnings for a stream = product-sale revenue + kudos/tips received.
const streamEarnings = (s = {}) => Number(s.revenue || 0) + Number(s.tip_total || 0)

// A stream belongs to a creator (not a shop) when it carries a creator_id and no shop_id.
const isCreatorStream = (s = {}) => !!s.creator_id && !s.shop_id

// A replay only exists once the upload has finished — recording_status can
// lag/mismatch recording_url in edge cases, so require both (same check the
// backend's public replay feed uses).
const isReplayReady = (s = {}) => s.recording_status === 'ready' && !!s.recording_url

const duration = (start, end) => {
  if (!start) return '—'
  const ms   = ((end ? new Date(end) : new Date()) - new Date(start))
  const mins = Math.floor(ms / 60000)
  const hrs  = Math.floor(mins / 60)
  if (hrs > 0) return `${hrs}h ${mins % 60}m`
  return `${mins}m`
}

const avatarBg = (str = '') => {
  const colors = ['#fde8d8','#dff5e8','#e8f4ff','#fef3d0','#ede8ff','#fce7f3']
  let h = 0
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h)
  return colors[Math.abs(h) % colors.length]
}
const initials = (name = '') => (name.trim().charAt(0) || '?').toUpperCase()

// ── Shared ────────────────────────────────────────────────────────────────────
const SS = {
  select: { border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" },
}

function StatCard({ label, value, accent, sub, live }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '18px 20px', flex: 1, position: 'relative', overflow: 'hidden' }}>
      {live && (
        <div style={{ position: 'absolute', top: 14, right: 14, display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', boxShadow: '0 0 0 2px rgba(239,68,68,0.25)', display: 'inline-block', animation: 'livePulse 1.5s infinite' }} />
          <span style={{ fontSize: 9, fontWeight: 700, color: '#ef4444', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>Live</span>
        </div>
      )}
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px', color: accent || '#111317' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 4, fontFamily: "'JetBrains Mono',monospace" }}>{sub}</div>}
    </div>
  )
}

// ── Live Stream Detail Drawer ─────────────────────────────────────────────────
function StreamDrawer({ stream, onClose, onEnd, onReplayDeleted }) {
  const navigate = useNavigate()
  const [tab,        setTab]        = useState('details')
  const [detail,     setDetail]     = useState(null)
  const [detailLoad, setDetailLoad] = useState(false)
  const [delLoad,    setDelLoad]    = useState(false)

  useEffect(() => {
    if (!stream) return
    setTab('details')
    setDetail(null)
    setDetailLoad(true)
    api.get(`/live-history/admin/streams/${stream.stream_id || stream.id}`)
      .then(res => {
        const s = res.data.stream
        setDetail(s)
        const isLive = s?.is_live || s?.status === 'live'
        if (!isLive && isReplayReady(s)) setTab('replay')
      })
      .catch(() => setDetail(stream))
      .finally(() => setDetailLoad(false))
  }, [stream?.stream_id, stream?.id])

  if (!stream) return null
  const d  = detail || stream
  const isLive = d.is_live || d.status === 'live'
  const hasReplay = !isLive && isReplayReady(d)
  const isCreator = isCreatorStream(d)

  const handleDeleteReplay = async () => {
    if (!window.confirm('Delete this replay? Viewers will no longer be able to watch it back.')) return
    setDelLoad(true)
    try {
      const id = d.stream_id || d.id
      await api.delete(`/live-history/admin/streams/${id}/recording`)
      setDetail(prev => ({ ...(prev || d), recording_url: null, recording_status: 'none' }))
      setTab('details')
      onReplayDeleted?.(id)
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete replay')
    } finally { setDelLoad(false) }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 200 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 500, background: '#fff', zIndex: 201, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 40px rgba(0,0,0,0.12)', animation: 'slideIn 0.22s ease' }}>
        <style>{`
          @keyframes slideIn { from { transform:translateX(40px);opacity:0 } to { transform:translateX(0);opacity:1 } }
          @keyframes livePulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(1.3)} }
        `}</style>

        {/* Header */}
        <div style={{ padding: '18px 20px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', gap: 12 }}>
          <ShopAvatar name={d.shop_name || d.host_name} avatar={d.shop_avatar} size={46} radius={10} fontSize={19} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.shop_name || d.host_name || '—'}</div>
              {isLive && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fef2f2', color: '#ef4444', fontSize: 9, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", padding: '2px 7px', borderRadius: 4, textTransform: 'uppercase', flexShrink: 0 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', animation: 'livePulse 1.5s infinite', display: 'inline-block' }} />
                  LIVE
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>
              {d.channel_name || d.stream_id || d.id} · {num(d.viewer_count || d.peak_viewers || 0)} viewers
            </div>
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94' }}>×</div>
        </div>

        {/* Quick stats strip */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e9e9e4', padding: '10px 20px', gap: 16 }}>
          {[
            ['👁', num(d.total_viewers || d.viewer_count || d.peak_viewers || 0), 'Views'],
            ['💬', num(d.total_messages || d.comment_count || d.comments?.length || 0), 'Comments'],
            ['❤️', num(d.total_likes || d.like_count || d.likes?.length || 0), 'Likes'],
            isCreator
              ? ['🎁', money(d.tip_total || 0), 'Kudos']
              : ['🛍', num(d.purchases?.length || d.order_count || 0), 'Purchased'],
            ['⏱', duration(d.started_at, d.ended_at), 'Duration'],
          ].map(([icon, val, lbl]) => (
            <div key={lbl} style={{ textAlign: 'center', flex: 1 }}>
              <div style={{ fontSize: 15 }}>{icon}</div>
              <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.3px' }}>{val}</div>
              <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.06em' }}>{lbl}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e9e9e4', padding: '0 20px' }}>
          {[
            ...(hasReplay ? [['replay','Replay']] : []),
            ['details','Details'],
            ...(isCreator ? [] : [['products','Products']]),
            ['comments','Comments'],['likes','Likes'],['viewers','Viewers'],
            isCreator ? ['kudos','Kudos'] : ['purchases','Purchases'],
          ].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ padding: '10px 13px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif", color: tab === id ? '#111317' : '#9a9a94', borderBottom: `2px solid ${tab === id ? '#e8440a' : 'transparent'}`, marginBottom: -1 }}>{label}</button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {detailLoad && <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '32px 0' }}>Loading…</div>}

          {/* ── Replay ── */}
          {!detailLoad && tab === 'replay' && (
            hasReplay
              ? (
                <div>
                  <video
                    src={d.recording_url}
                    controls
                    playsInline
                    poster={d.thumbnail_url || d.shop_avatar || undefined}
                    style={{ width: '100%', maxHeight: 400, borderRadius: 10, background: '#000', display: 'block' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
                    <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                      Recorded {fmtTime(d.started_at)} · {duration(d.started_at, d.ended_at)}
                    </div>
                    <button
                      onClick={handleDeleteReplay}
                      disabled={delLoad}
                      style={{ height: 30, padding: '0 12px', borderRadius: 7, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: delLoad ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: delLoad ? 0.6 : 1 }}>
                      {delLoad ? 'Deleting…' : '🗑 Delete Replay'}
                    </button>
                  </div>
                </div>
              )
              : <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No replay available for this stream.</div>
          )}

          {/* ── Details ── */}
          {!detailLoad && tab === 'details' && (
            <>
              {[
                ['Stream ID',      d.stream_id || d.id],
                ['Shop',           d.shop_name || '—'],
                ['Host',           d.host_name || d.username || '—'],
                ['Channel',        d.channel_name || '—'],
                ['Status',         isLive ? <span style={{ color: '#ef4444', fontWeight: 700 }}>🔴 Live Now</span> : <span style={{ color: '#9a9a94' }}>Ended</span>],
                ['Viewers',        num(d.viewer_count || d.peak_viewers || 0)],
                ['Peak Viewers',   num(d.peak_viewers || 0)],
                ['Comments',       num(d.comment_count || d.total_messages || 0)],
                ['Likes',          num(d.like_count || d.total_likes || 0)],
                ['Purchases',      num(d.purchases?.length || d.order_count || 0)],
                ['Revenue',        money(streamEarnings(d))],
                ['Duration',       duration(d.started_at, d.ended_at)],
                ['Started',        fmtTime(d.started_at)],
                ['Ended',          d.ended_at ? fmtTime(d.ended_at) : '—'],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #f3f3f0' }}>
                  <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600 }}>{k}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, fontFamily: typeof v === 'string' ? "'JetBrains Mono',monospace" : 'inherit', color: '#111317' }}>{v}</span>
                </div>
              ))}
            </>
          )}

          {/* ── Products ── */}
          {!detailLoad && tab === 'products' && (
            !d.products?.length
              ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>📦</div>
                  <div style={{ fontSize: 13, color: '#9a9a94' }}>No products were pinned in this stream.</div>
                  <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 4 }}>Products appear here when the seller pins them during the live.</div>
                </div>
              )
              : (() => {
                  const purchaseCount = d.purchases?.length || 0
                  return (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <span style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                          {d.products.length} product{d.products.length !== 1 ? 's' : ''} featured · click to view in shop
                        </span>
                        {purchaseCount > 0 && (
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d', fontFamily: "'JetBrains Mono',monospace", background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 5, padding: '2px 8px' }}>
                            🛍 {purchaseCount} purchase{purchaseCount !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      {d.products.map((p, i) => {
                        // Parse image — may be JSON array string or direct URL
                        let imgSrc = null
                        try {
                          const raw = p.images ?? p.image
                          if (typeof raw === 'string' && raw.startsWith('[')) {
                            imgSrc = JSON.parse(raw)[0]
                          } else if (Array.isArray(raw)) {
                            imgSrc = raw[0]
                          } else {
                            imgSrc = raw
                          }
                        } catch {}

                        // Count purchases that reference this product
                        const soldCount = (d.purchases || []).filter(pur =>
                          pur.prod_id === p.prod_id || pur.product_id === p.prod_id ||
                          pur.items?.some(it => it.prod_id === p.prod_id || it.product_id === p.prod_id)
                        ).length

                        const handleProductClick = () => {
                          if (p.shop_id) {
                            onClose()
                            navigate(`/sellers?highlight=${p.shop_id}`)
                          }
                        }

                        return (
                          <div key={p.prod_id || i}
                            onClick={handleProductClick}
                            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px', borderRadius: 10, marginBottom: 8, border: `1px solid ${soldCount > 0 ? '#bbf7d0' : '#e9e9e4'}`, cursor: p.shop_id ? 'pointer' : 'default', transition: 'all 0.12s', background: soldCount > 0 ? '#f0fdf4' : '#fff' }}
                            onMouseEnter={e => { if(p.shop_id){ e.currentTarget.style.background='#fafaf8'; e.currentTarget.style.borderColor='#e8440a44' }}}
                            onMouseLeave={e => { e.currentTarget.style.background = soldCount > 0 ? '#f0fdf4' : '#fff'; e.currentTarget.style.borderColor = soldCount > 0 ? '#bbf7d0' : '#e9e9e4' }}
                          >
                            {/* Product image */}
                            <div style={{ width: 52, height: 52, borderRadius: 9, background: '#f3f3f0', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                              {imgSrc
                                ? <img src={imgSrc} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display='none' }} />
                                : '📦'
                              }
                            </div>

                            {/* Info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                <div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name || 'Product'}</div>
                                {/* quick_live / standard badge */}
                                {p.product_type === 'quick_live' ? (
                                  <span style={{ fontSize: 9, fontWeight: 800, color: '#e8440a', background: '#fff3f0', border: '1px solid #fdd0c0', borderRadius: 4, padding: '1px 6px', fontFamily: "'JetBrains Mono',monospace", flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>⚡ Quick Live</span>
                                ) : (
                                  <span style={{ fontSize: 9, fontWeight: 700, color: '#6b7280', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 6px', fontFamily: "'JetBrains Mono',monospace", flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Standard</span>
                                )}
                                {soldCount > 0 && (
                                  <span style={{ fontSize: 9, fontWeight: 800, color: '#15803d', background: '#dcfce7', borderRadius: 4, padding: '1px 5px', fontFamily: "'JetBrains Mono',monospace", flexShrink: 0 }}>
                                    {soldCount}× SOLD
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                                {p.category && <span style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{p.category}</span>}
                                {p.prod_id  && <span style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{p.prod_id}</span>}
                              </div>
                              {p.pinnedAt && (
                                <div style={{ fontSize: 10, color: '#9a9a94', marginTop: 2 }}>Pinned {fmtTime(p.pinnedAt)}</div>
                              )}
                            </div>

                            {/* Price + stock */}
                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                              <div style={{ fontSize: 14, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>
                                {p.price ? `${Number(p.price).toLocaleString()} RWF` : '—'}
                              </div>
                              {p.quantity != null && (
                                <div style={{ fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: p.quantity > 0 ? '#15803d' : '#b91c1c', marginTop: 2 }}>
                                  {p.quantity > 0 ? `${p.quantity} in stock` : 'Out of stock'}
                                </div>
                              )}
                              {p.shop_id && (
                                <div style={{ fontSize: 10, color: '#e8440a', fontWeight: 700, marginTop: 3 }}>View shop →</div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )
                })()
          )}

          {/* ── Comments ── */}
          {!detailLoad && tab === 'comments' && (
            !d.comments?.length
              ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No comments recorded for this stream.</div>
              : <div>
                  <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginBottom: 12 }}>{d.comments.length} comment{d.comments.length !== 1 ? 's' : ''}</div>
                  {d.comments.map((c, i) => (
                    <div key={c.id || i} style={{ padding: '10px 0', borderBottom: '1px solid #f3f3f0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <div style={{ width: 26, height: 26, borderRadius: 6, background: avatarBg(c.username || ''), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0 }}>
                          {initials(c.username)}
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700 }}>{c.username || 'Anonymous'}</span>
                        <span style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginLeft: 'auto' }}>{fmtTime(c.sentAt || c.created_at)}</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.5, paddingLeft: 34 }}>{c.message || c.comment}</div>
                    </div>
                  ))}
                </div>
          )}

          {/* ── Likes ── */}
          {!detailLoad && tab === 'likes' && (
            !d.likes?.length
              ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No likes recorded for this stream.</div>
              : <div>
                  <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginBottom: 12 }}>{d.likes.length} like{d.likes.length !== 1 ? 's' : ''} · {d.total_likes || d.likes.length} total</div>
                  {d.likes.map((l, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f3f3f0' }}>
                      <div style={{ width: 28, height: 28, borderRadius: 6, background: avatarBg(l.username || ''), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                        {initials(l.username)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 700 }}>{l.username || 'Anonymous'}</div>
                        <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmtTime(l.likedAt)}</div>
                      </div>
                      <span style={{ fontSize: 16 }}>❤️</span>
                    </div>
                  ))}
                </div>
          )}

          {/* ── Viewers ── */}
          {!detailLoad && tab === 'viewers' && (
            !d.viewers?.length
              ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No viewer records for this stream.</div>
              : <div>
                  <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginBottom: 12 }}>{d.viewers.length} unique viewer{d.viewers.length !== 1 ? 's' : ''} · peak {d.peak_viewers || 0}</div>
                  {d.viewers.map((v, i) => {
                    const watchMs = v.leftAt && v.joinedAt ? new Date(v.leftAt) - new Date(v.joinedAt) : null
                    const watchMins = watchMs ? Math.floor(watchMs / 60000) : null
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid #f3f3f0' }}>
                        <div style={{ width: 28, height: 28, borderRadius: 6, background: avatarBg(v.username || ''), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                          {initials(v.username)}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 700 }}>{v.username || 'Anonymous'}</div>
                          <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                            Joined {fmtTime(v.joinedAt)}{v.leftAt ? ` · Left ${fmtTime(v.leftAt)}` : ' · Still watching'}
                          </div>
                        </div>
                        {watchMins != null && (
                          <div style={{ fontSize: 11, fontWeight: 700, color: watchMins >= 10 ? '#15803d' : '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textAlign: 'right' }}>
                            {watchMins}m
                          </div>
                        )}
                        {!v.leftAt && (
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', display: 'inline-block', flexShrink: 0 }} />
                        )}
                      </div>
                    )
                  })}
                </div>
          )}

          {/* ── Kudos ── */}
          {!detailLoad && tab === 'kudos' && (
            !d.tip_total
              ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>🎁</div>
                  No kudos received during this stream.
                </div>
              : (
                <div style={{ textAlign: 'center', padding: '32px 0' }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>🎁</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#15803d' }}>{money(d.tip_total)}</div>
                  <div style={{ fontSize: 12, color: '#9a9a94', marginTop: 4 }}>in kudos (tips) received from viewers</div>
                </div>
              )
          )}

          {/* ── Purchases ── */}
          {!detailLoad && tab === 'purchases' && (
            !d.purchases?.length
              ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>🛍</div>
                  No purchases recorded for this stream.
                </div>
              : (
                <div>
                  <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginBottom: 12 }}>
                    {d.purchases.length} purchase{d.purchases.length !== 1 ? 's' : ''}
                    {d.purchases.some(p => p.total_price || p.total_amount || p.amount)
                      ? ` · ${money(d.purchases.reduce((sum, p) => sum + Number(p.total_price || p.total_amount || p.amount || 0), 0))} total`
                      : ''}
                  </div>
                  {d.purchases.map((p, i) => (
                    <div key={p.order_id || p.id || i} style={{ background: '#f9f9f7', borderRadius: 10, padding: '12px 14px', marginBottom: 8, border: '1px solid #e9e9e4' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 28, height: 28, borderRadius: 6, background: avatarBg(p.buyer_name || p.username || ''), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                            {initials(p.buyer_name || p.username)}
                          </div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 700 }}>{p.buyer_name || p.username || 'Anonymous'}</div>
                            {(p.order_id || p.id) && (
                              <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{p.order_id || p.id}</div>
                            )}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          {(p.total_price || p.total_amount || p.amount) && (
                            <div style={{ fontSize: 14, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>
                              {money(p.total_price || p.total_amount || p.amount)}
                            </div>
                          )}
                          {p.status && (
                            <div style={{ fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: p.status === 'completed' ? '#15803d' : p.status === 'pending' ? '#d97706' : '#9a9a94', marginTop: 2 }}>
                              {p.status.toUpperCase()}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Product(s) in this purchase */}
                      {(p.product_name || p.name || p.items?.length > 0) && (
                        <div style={{ fontSize: 11, color: '#374151', background: '#fff', borderRadius: 6, padding: '6px 10px', border: '1px solid #e9e9e4' }}>
                          {p.items?.length > 0
                            ? p.items.map((item, j) => (
                                <div key={j} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span>{item.name || item.product_name || 'Product'}</span>
                                  <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>×{item.qty || item.quantity || 1}</span>
                                </div>
                              ))
                            : <span>{p.product_name || p.name}</span>
                          }
                        </div>
                      )}
                      {p.purchased_at || p.created_at ? (
                        <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 6 }}>{fmtTime(p.purchased_at || p.created_at)}</div>
                      ) : null}
                    </div>
                  ))}
                </div>
              )
          )}
        </div>

        {/* Footer */}
        {isLive && (
          <div style={{ padding: '14px 20px', borderTop: '1px solid #e9e9e4' }}>
            <button
              onClick={() => onEnd(d)}
              style={{ width: '100%', height: 42, borderRadius: 8, border: 'none', cursor: 'pointer', background: '#fef2f2', color: '#b91c1c', fontSize: 13, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              ⏹ Force End Stream
            </button>
          </div>
        )}
      </div>
    </>
  )
}

// ── End Stream Confirm ────────────────────────────────────────────────────────
function EndModal({ stream, onClose, onConfirm, loading }) {
  if (!stream) return null
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 300 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 400, background: '#fff', borderRadius: 14, zIndex: 301, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'popIn 0.2s ease' }}>
        <style>{`@keyframes popIn { from { opacity:0; transform:translate(-50%,-48%) scale(0.97) } to { opacity:1; transform:translate(-50%,-50%) scale(1) } }`}</style>
        <div style={{ padding: '28px 28px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⏹</div>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Force End Stream?</div>
          <div style={{ fontSize: 13, color: '#9a9a94', lineHeight: 1.7 }}>
            <b>{stream.shop_name || stream.host_name}</b>'s live stream will be immediately terminated. The host will be notified.
          </div>
        </div>
        <div style={{ padding: '0 28px 24px', display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
            {loading ? 'Ending…' : 'Yes, End Stream'}
          </button>
        </div>
      </div>
    </>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function LiveStreams() {
  const [streams,      setStreams]      = useState([])
  const [history,      setHistory]     = useState([])
  const [loading,      setLoading]     = useState(true)
  const [histLoad,     setHistLoad]    = useState(true)
  const [error,        setError]       = useState('')
  const [tab,          setTab]         = useState('live')  // 'live' | 'history'
  const [search,       setSearch]      = useState('')
  const [page,         setPage]        = useState(1)
  const [totalPages,   setTotalPages]  = useState(1)
  const [totalCount,   setTotalCount]  = useState(0)
  const [stats,        setStats]       = useState({ live_now: 0, today: 0, total_viewers: 0, total_revenue: 0 })
  const [selected,     setSelected]    = useState(null)
  const [ending,       setEnding]      = useState(null)
  const [endLoad,      setEndLoad]     = useState(false)
  const refreshRef = useRef(null)

  const PER_PAGE = 15

  // ── Fetch live streams ──
  const fetchLive = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/live-history/admin/streams/live')
      setStreams(res.data.streams || [])
      setStats(res.data.stats    || { live_now: 0, today: 0, total_viewers: 0, total_revenue: 0 })
    } catch {
      // Mock live streams
      setStreams(Array.from({ length: 5 }, (_, i) => ({
        id:            i + 1,
        stream_id:     `stream_${i + 1}`,
        shop_name:     `Shop ${i + 1}`,
        host_name:     `Host ${i + 1}`,
        channel_name:  `channel_${i + 1}`,
        is_live:       true,
        viewer_count:  Math.floor(Math.random() * 2000) + 50,
        peak_viewers:  Math.floor(Math.random() * 3000) + 100,
        like_count:    Math.floor(Math.random() * 500),
        comment_count: Math.floor(Math.random() * 300),
        order_count:   Math.floor(Math.random() * 20),
        revenue:       Math.random() * 500,
        started_at:    new Date(Date.now() - Math.random() * 7200000).toISOString(),
        products: [], comments: [], orders: [],
      })))
      setStats({ live_now: 5, today: 18, total_viewers: 4812, total_revenue: 2840 })
    } finally { setLoading(false) }
  }, [])

  // ── Fetch history ──
  const fetchHistory = useCallback(async () => {
    setHistLoad(true)
    try {
      const params = new URLSearchParams({ page, limit: PER_PAGE, ...(search && { search }) })
      const res = await api.get(`/live-history/admin/streams/history?${params}`)
      setHistory(res.data.streams   || [])
      setTotalPages(res.data.totalPages || 1)
      setTotalCount(res.data.total      || 0)
    } catch {
      setHistory(Array.from({ length: 10 }, (_, i) => ({
        id:            i + 100,
        stream_id:     `ended_${i + 1}`,
        shop_name:     `Shop ${i + 1}`,
        host_name:     `Host ${i + 1}`,
        channel_name:  `channel_${i + 1}`,
        is_live:       false,
        status:        'ended',
        viewer_count:  0,
        peak_viewers:  Math.floor(Math.random() * 1500) + 100,
        like_count:    Math.floor(Math.random() * 300),
        comment_count: Math.floor(Math.random() * 150),
        order_count:   Math.floor(Math.random() * 15),
        revenue:       Math.random() * 400,
        started_at:    new Date(Date.now() - (i + 1) * 86400000).toISOString(),
        ended_at:      new Date(Date.now() - (i + 1) * 86400000 + Math.random() * 7200000).toISOString(),
      })))
      setTotalPages(3); setTotalCount(30)
    } finally { setHistLoad(false) }
  }, [page, search])

  useEffect(() => {
    fetchLive()
    fetchHistory()
    // Auto-refresh live every 30s
    refreshRef.current = setInterval(fetchLive, 30000)
    return () => clearInterval(refreshRef.current)
  }, [fetchLive, fetchHistory])

  useEffect(() => { setPage(1) }, [search])

  const handleEndStream = async () => {
    if (!ending) return
    setEndLoad(true)
    try {
      await api.post(`/live-history/admin/streams/${ending.stream_id || ending.id}/end`)
      setStreams(prev => prev.filter(s => (s.stream_id || s.id) !== (ending.stream_id || ending.id)))
      setStats(prev => ({ ...prev, live_now: Math.max(0, prev.live_now - 1) }))
      setEnding(null)
      setSelected(null)
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to end stream')
    } finally { setEndLoad(false) }
  }

  const liveFiltered = streams.filter(s =>
    !search || [s.shop_name, s.host_name, s.channel_name].some(f => f?.toLowerCase().includes(search.toLowerCase()))
  )

  const histFiltered = history.filter(s =>
    !search || [s.shop_name, s.host_name, s.channel_name].some(f => f?.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <style>{`
        @keyframes livePulse { 0%,100%{opacity:1;box-shadow:0 0 0 0 rgba(239,68,68,0.4)} 50%{opacity:0.7;box-shadow:0 0 0 6px rgba(239,68,68,0)} }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Live Stream Management</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Monitor active streams, history and engagement.</div>
        </div>
        <button onClick={() => { fetchLive(); fetchHistory() }} style={{ height: 38, padding: '0 16px', borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", display: 'flex', alignItems: 'center', gap: 6 }}>
          ↻ Refresh
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
        <StatCard label="Live Now"       value={stats.live_now}                               accent="#ef4444" live />
        <StatCard label="Today's Streams" value={stats.today}                                                     />
        <StatCard label="Total Viewers"  value={num(stats.total_viewers)}                    accent="#1d4ed8"    />
        <StatCard label="Today Revenue"  value={money(stats.total_revenue)}                  accent="#15803d"    />
      </div>

      {/* Tabs + Search */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: 5, gap: 2 }}>
          {[['live', `🔴 Live (${streams.length})`], ['history', '📋 History']].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ padding: '7px 18px', borderRadius: 7, border: 'none', background: tab === id ? '#111317' : 'transparent', color: tab === id ? '#fff' : '#9a9a94', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'all 0.15s', whiteSpace: 'nowrap' }}>{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 8, padding: '8px 13px' }}>
          <span style={{ fontSize: 14, color: '#9a9a94' }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by shop, host or channel…" style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#111317', flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
          {search && <span onClick={() => setSearch('')} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 16 }}>×</span>}
        </div>
      </div>

      {/* ── LIVE TAB ── */}
      {tab === 'live' && (
        loading
          ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '60px 0' }}>Loading live streams…</div>
          : liveFiltered.length === 0
            ? (
              <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '60px 32px', textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📡</div>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>No live streams right now</div>
                <div style={{ fontSize: 13, color: '#9a9a94' }}>Active streams will appear here in real time.</div>
              </div>
            )
            : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
                {liveFiltered.map(s => (
                  <LiveCard key={s.stream_id || s.id} stream={s} onClick={() => setSelected(s)} onEnd={() => setEnding(s)} />
                ))}
              </div>
            )
      )}

      {/* ── HISTORY TAB ── */}
      {tab === 'history' && (
        <>
          <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden' }}>
            {/* Head */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 0.7fr 0.7fr 0.7fr 0.6fr 0.8fr 0.7fr 0.8fr 0.6fr', padding: '10px 20px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
              {['Shop / Host','Channel','Views','Comments','Likes','Orders','Revenue','Duration','Date','Replay'].map(h => (
                <div key={h} style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
              ))}
            </div>

            {histLoad ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading history…</div>
            ) : histFiltered.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No stream history found.</div>
            ) : (
              histFiltered.map((s, i) => (
                <div
                  key={s.stream_id || s.id}
                  onClick={() => setSelected(s)}
                  style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 0.7fr 0.7fr 0.7fr 0.6fr 0.8fr 0.7fr 0.8fr 0.6fr', padding: '13px 20px', borderBottom: i < histFiltered.length - 1 ? '1px solid #f3f3f0' : 'none', cursor: 'pointer', transition: 'background 0.12s', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafaf8'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <ShopAvatar name={s.shop_name} avatar={s.shop_avatar} size={30} radius={7} fontSize={12} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700 }}>{s.shop_name || '—'}</div>
                      <div style={{ fontSize: 11, color: '#9a9a94' }}>{s.host_name || '—'}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.channel_name || '—'}</div>
                  <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{num(s.total_viewers || s.peak_viewers || 0)}</div>
                  <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{num(s.comment_count || s.total_messages || 0)}</div>
                  <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{num(s.total_likes || s.like_count || 0)}</div>
                  <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>{num(s.order_count || 0)}</div>
                  <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, color: '#15803d' }}>{money(streamEarnings(s))}</div>
                  <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{duration(s.started_at, s.ended_at)}</div>
                  <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmt(s.started_at)}</div>
                  <div>
                    {isReplayReady(s)
                      ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 9, fontWeight: 800, color: '#e8440a', background: '#fff3f0', border: '1px solid #fdd0c0', borderRadius: 4, padding: '2px 7px', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.04em' }}>▶ Replay</span>
                      : <span style={{ fontSize: 12, color: '#c8c0c0' }}>—</span>
                    }
                  </div>
                </div>
              ))
            )}
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
              <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>Page {page} of {totalPages} · {totalCount.toLocaleString()} streams</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <PagBtn label="← Prev" disabled={page === 1}          onClick={() => setPage(p => p - 1)} />
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
                  return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} />
                })}
                <PagBtn label="Next →" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
              </div>
            </div>
          )}
        </>
      )}

      {/* Drawer + Modals */}
      <StreamDrawer
        stream={selected}
        onClose={() => setSelected(null)}
        onEnd={s => { setEnding(s); setSelected(null) }}
        onReplayDeleted={id => setHistory(prev => prev.map(s =>
          (s.stream_id || s.id) === id ? { ...s, recording_url: null, recording_status: 'none' } : s
        ))}
      />
      <EndModal stream={ending} onClose={() => setEnding(null)} onConfirm={handleEndStream} loading={endLoad} />
    </div>
  )
}

// ── Shop Avatar — image if available, letter fallback ────────────────────────
function ShopAvatar({ name = '', avatar, size = 40, radius = 9, fontSize = 16 }) {
  const [imgErr, setImgErr] = useState(false)
  if (avatar && !imgErr) {
    return (
      <img
        src={avatar} alt={name}
        onError={() => setImgErr(true)}
        style={{ width: size, height: size, borderRadius: radius, objectFit: 'cover', flexShrink: 0 }}
      />
    )
  }
  return (
    <div style={{ width: size, height: size, borderRadius: radius, background: avatarBg(name), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize, fontWeight: 800, color: '#111317', flexShrink: 0 }}>
      {initials(name)}
    </div>
  )
}

// ── Live Stream Card ──────────────────────────────────────────────────────────
function LiveCard({ stream: s, onClick, onEnd }) {
  const [elapsed, setElapsed] = useState(duration(s.started_at, null))

  useEffect(() => {
    const t = setInterval(() => setElapsed(duration(s.started_at, null)), 10000)
    return () => clearInterval(t)
  }, [s.started_at])

  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 12, overflow: 'hidden', cursor: 'pointer', transition: 'all 0.15s' }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)' }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none' }}
    >
      {/* Dark header */}
      <div style={{ background: '#111317', padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 12 }} onClick={onClick}>
        <ShopAvatar name={s.shop_name} avatar={s.shop_avatar} size={40} radius={9} fontSize={16} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.shop_name || '—'}</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', fontFamily: "'JetBrains Mono',monospace" }}>{s.channel_name || s.host_name || '—'}</div>
        </div>
        {/* Live badge */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#ef4444', borderRadius: 5, padding: '4px 9px', flexShrink: 0 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', animation: 'livePulse 1.5s infinite', display: 'inline-block' }} />
          <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', fontFamily: "'JetBrains Mono',monospace" }}>LIVE</span>
        </div>
      </div>

      {/* Stats grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 0, borderBottom: '1px solid #e9e9e4' }} onClick={onClick}>
        {[
          ['👁', num(s.viewer_count || 0), 'Viewers'],
          ['❤️', num(s.like_count   || 0), 'Likes'],
          ['💬', num(s.comment_count|| 0), 'Comments'],
          ['⏱', elapsed,                   'Duration'],
        ].map(([icon, val, lbl], i) => (
          <div key={lbl} style={{ padding: '12px 10px', textAlign: 'center', borderRight: i < 3 ? '1px solid #e9e9e4' : 'none' }}>
            <div style={{ fontSize: 14 }}>{icon}</div>
            <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.3px', marginTop: 1 }}>{val}</div>
            <div style={{ fontSize: 9, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.06em' }}>{lbl}</div>
          </div>
        ))}
      </div>

      {/* Orders + Revenue row */}
      <div style={{ padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }} onClick={onClick}>
        <div style={{ fontSize: 12, color: '#9a9a94' }}>
          <span style={{ fontWeight: 700, color: '#111317' }}>{num(s.order_count || 0)}</span> orders · <span style={{ fontWeight: 700, color: '#15803d' }}>{money(streamEarnings(s))}</span>
        </div>
        <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{fmtTime(s.started_at)}</div>
      </div>

      {/* Footer action */}
      <div style={{ padding: '10px 14px', borderTop: '1px solid #e9e9e4', display: 'flex', gap: 8 }}>
        <button onClick={onClick} style={{ flex: 1, height: 34, borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>View Details</button>
        <button onClick={e => { e.stopPropagation(); onEnd() }} style={{ height: 34, padding: '0 14px', borderRadius: 7, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>End</button>
      </div>
    </div>
  )
}

function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ minWidth: 34, height: 34, borderRadius: 7, border: '1px solid #e9e9e4', background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#9a9a94' : '#111317', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', padding: '0 10px', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: disabled ? 0.4 : 1 }}>
      {label}
    </button>
  )
}